import type { WritingExpressionCategoryId, WritingExpressionItem } from '@examflow/exams';
import {
  WRITING_EXPRESSION_CATEGORY_IDS,
  WRITING_EXPRESSION_CATEGORY_LABELS,
  getWritingExpressionsForLanguage,
  hasWritingExpressionPack,
} from '@examflow/exams';

export {
  WRITING_EXPRESSION_CATEGORY_IDS,
  WRITING_EXPRESSION_CATEGORY_LABELS,
  getWritingExpressionsForLanguage,
  hasWritingExpressionPack,
};

const DEFAULT_INLINE_CATEGORIES: WritingExpressionCategoryId[] = [
  'continue',
  'exemplify',
  'summarize',
];

/** Map rubric weak dimension → expression categories to open first. */
export function expressionCategoriesForWritingDimension(
  dimensionId: string | null | undefined
): WritingExpressionCategoryId[] {
  switch (String(dimensionId ?? '')
    .trim()
    .toLowerCase()) {
    case 'coherence':
    case 'cc':
    case 'cohesion':
    case 'organization':
      return ['continue', 'parallel_add', 'contrast_turn', 'contrast_concede', 'summarize'];
    case 'vocabulary':
    case 'lr':
    case 'lexical':
    case 'lexis':
      return ['progressive', 'exemplify', 'emphasize', 'restate', 'compare'];
    case 'grammar':
    case 'gra':
    case 'accuracy':
    case 'range':
      return ['restate', 'compare', 'emphasize', 'continue'];
    case 'task':
    case 'tr':
    case 'content':
    case 'task_response':
      return ['exemplify', 'progressive', 'summarize', 'emphasize'];
    default:
      return [...DEFAULT_INLINE_CATEGORIES];
  }
}

export function filterWritingExpressions(
  items: readonly WritingExpressionItem[],
  opts: {
    category?: WritingExpressionCategoryId | null;
    query?: string;
  } = {}
): WritingExpressionItem[] {
  const query = String(opts.query ?? '')
    .trim()
    .toLowerCase();
  return items.filter((item) => {
    if (opts.category && item.category !== opts.category) return false;
    if (!query) return true;
    const hay = `${item.text} ${item.gloss ?? ''}`.toLowerCase();
    return hay.includes(query);
  });
}

export function defaultExpressionCategory(
  items: readonly WritingExpressionItem[],
  preferred?: WritingExpressionCategoryId | null
): WritingExpressionCategoryId {
  if (preferred && items.some((item) => item.category === preferred)) {
    return preferred;
  }
  for (const category of WRITING_EXPRESSION_CATEGORY_IDS) {
    if (items.some((item) => item.category === category)) return category;
  }
  return WRITING_EXPRESSION_CATEGORY_IDS[0];
}

/**
 * Pick 2–3 bank phrases for the weak dimension (stable order; no AI).
 * Empty when the Language has no expression pack.
 */
export function pickInlineWritingExpressions(
  languageId: string | null | undefined,
  dimensionId: string | null | undefined,
  limit = 3
): WritingExpressionItem[] {
  const capped = Math.max(1, Math.min(5, Math.trunc(limit) || 3));
  const items = getWritingExpressionsForLanguage(languageId);
  if (items.length === 0) return [];

  const categories = expressionCategoriesForWritingDimension(dimensionId);
  const preferred = items.filter((item) => categories.includes(item.category));
  const pool = preferred.length > 0 ? preferred : [...items];
  const picked: WritingExpressionItem[] = [];
  const seen = new Set<string>();

  for (const category of categories) {
    for (const item of pool) {
      if (item.category !== category) continue;
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      picked.push(item);
      if (picked.length >= capped) return picked;
    }
  }

  for (const item of pool) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    picked.push(item);
    if (picked.length >= capped) break;
  }
  return picked;
}
